import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { BrandMark } from "../components/BrandMark";
import { toast } from "sonner";
import { KeyRound, Lock, User, ArrowRight, ShieldCheck, HelpCircle, Info, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatErr(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default function CustomerRecover() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1: enter ID, 2: DOB/answer/new cred
    const [customerCode, setCustomerCode] = useState("");
    const [question, setQuestion] = useState("");
    const [dob, setDob] = useState("");
    const [answer, setAnswer] = useState("");
    const [method, setMethod] = useState("password"); // "password" | "pin"
    const [newCred, setNewCred] = useState("");
    const [newCred2, setNewCred2] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        document.title = "Recover Access | Lokdhara Microfinance";
    }, []);

    const fetchQuestion = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/customer-auth/security-question`, {
                params: { code: customerCode.trim() },
            });
            if (!data.available) {
                setErr(data.message || "Recovery not available. Please contact your branch.");
                return;
            }
            setQuestion(data.question);
            setStep(2);
        } catch (e2) {
            setErr(formatErr(e2.response?.data?.detail) || e2.message);
        } finally {
            setLoading(false);
        }
    };

    const submitRecover = async (e) => {
        e.preventDefault();
        setErr("");
        if (newCred !== newCred2) {
            setErr(method === "pin" ? "PINs do not match" : "Passwords do not match");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/customer-auth/recover`, {
                customer_code: customerCode.trim(),
                dob,
                answer,
                reset_method: method,
                new_credential: newCred,
            });
            toast.success("Your " + (method === "pin" ? "PIN" : "password") + " has been reset. Please sign in.");
            navigate("/customer-login");
        } catch (e2) {
            setErr(formatErr(e2.response?.data?.detail) || e2.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-white via-brand-soft/40 to-brand-tint/60 flex flex-col">
            <header className="max-w-7xl w-full mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
                <Link to="/home" className="flex items-center" data-testid="rec-brand">
                    <BrandMark size={48} className="sm:!h-12" />
                </Link>
                <div className="text-sm text-slate-500">
                    Remembered it?{" "}
                    <Link
                        to="/customer-login"
                        className="text-brand-royal font-semibold hover:underline"
                        data-testid="rec-back-to-login"
                    >
                        Back to Sign In
                    </Link>
                </div>
            </header>

            <main className="flex-1 grid place-items-center px-4 sm:px-6 py-10">
                <Card className="w-full max-w-md rounded-3xl border-slate-100 shadow-card p-6 sm:p-8"
                    data-testid="rec-card"
                >
                    <div className="flex items-center gap-3">
                        <div className="h-11 w-11 rounded-xl bg-brand-soft grid place-items-center">
                            <HelpCircle className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                        </div>
                        <div>
                            <h1 className="font-display text-xl sm:text-2xl font-bold text-brand-navy">
                                Forgot Password / PIN?
                            </h1>
                            <p className="text-xs text-slate-500">
                                {step === 1
                                    ? "Enter your Customer ID to begin."
                                    : "Verify your details to set a new credential."}
                            </p>
                        </div>
                    </div>

                    {step === 1 ? (
                        <form className="mt-6 space-y-4" onSubmit={fetchQuestion} data-testid="rec-step1-form">
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    Customer ID
                                </label>
                                <div className="relative mt-2">
                                    <User className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                    <Input
                                        value={customerCode}
                                        onChange={(e) => setCustomerCode(e.target.value.toUpperCase())}
                                        placeholder="e.g. LMF0001"
                                        className="pl-9 h-11 rounded-xl"
                                        required
                                        data-testid="rec-customer-code"
                                    />
                                </div>
                            </div>

                            {err && (
                                <div className="text-sm text-rose-600 bg-rose-50 rounded-xl px-3 py-2"
                                    data-testid="rec-step1-error"
                                >
                                    {err}
                                </div>
                            )}

                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full rounded-full h-12 bg-brand-royal hover:bg-brand-hover text-white text-base"
                                data-testid="rec-step1-submit"
                            >
                                {loading ? "Checking…" : "Continue"} <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>

                            <div className="flex items-start gap-2 text-xs text-slate-500 italic">
                                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                <span>
                                    If recovery isn&apos;t set up for your account, please visit the branch
                                    or contact our team for a manual reset.
                                </span>
                            </div>
                        </form>
                    ) : (
                        <form className="mt-6 space-y-4" onSubmit={submitRecover} data-testid="rec-step2-form">
                            <div className="rounded-xl bg-brand-soft/60 p-3 text-sm text-slate-700"
                                data-testid="rec-question"
                            >
                                <div className="text-[11px] uppercase tracking-wider font-semibold text-brand-royal">
                                    Security Question
                                </div>
                                <div className="mt-1 font-semibold text-slate-900">{question}</div>
                            </div>

                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    Date of Birth
                                </label>
                                <div className="relative mt-2">
                                    <Calendar className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                    <Input
                                        type="date"
                                        value={dob}
                                        onChange={(e) => setDob(e.target.value)}
                                        className="pl-9 h-11 rounded-xl"
                                        required
                                        data-testid="rec-dob"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    Security Answer
                                </label>
                                <Input
                                    value={answer}
                                    onChange={(e) => setAnswer(e.target.value)}
                                    placeholder="Your answer"
                                    className="mt-2 h-11 rounded-xl"
                                    required
                                    data-testid="rec-answer"
                                />
                            </div>

                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    Reset
                                </label>
                                <div className="mt-2 grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => { setMethod("password"); setNewCred(""); setNewCred2(""); }}
                                        className={`h-11 rounded-xl border text-sm font-semibold transition ${
                                            method === "password"
                                                ? "bg-brand-navy text-white border-brand-navy"
                                                : "bg-white text-slate-700 border-slate-200 hover:border-brand-royal"
                                        }`}
                                        data-testid="rec-method-password"
                                    >
                                        Password
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setMethod("pin"); setNewCred(""); setNewCred2(""); }}
                                        className={`h-11 rounded-xl border text-sm font-semibold transition ${
                                            method === "pin"
                                                ? "bg-brand-navy text-white border-brand-navy"
                                                : "bg-white text-slate-700 border-slate-200 hover:border-brand-royal"
                                        }`}
                                        data-testid="rec-method-pin"
                                    >
                                        Security PIN
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    New {method === "pin" ? "PIN" : "Password"}
                                </label>
                                <div className="relative mt-2">
                                    {method === "pin" ? (
                                        <KeyRound className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                    ) : (
                                        <Lock className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                    )}
                                    <Input
                                        type="password"
                                        inputMode={method === "pin" ? "numeric" : "text"}
                                        maxLength={method === "pin" ? 6 : 128}
                                        pattern={method === "pin" ? "[0-9]*" : undefined}
                                        value={newCred}
                                        onChange={(e) => setNewCred(e.target.value)}
                                        placeholder={method === "pin" ? "New 4–6 digit PIN" : "New password (min 6)"}
                                        className="pl-9 h-11 rounded-xl"
                                        required
                                        data-testid="rec-new-cred"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    Confirm New {method === "pin" ? "PIN" : "Password"}
                                </label>
                                <Input
                                    type="password"
                                    inputMode={method === "pin" ? "numeric" : "text"}
                                    maxLength={method === "pin" ? 6 : 128}
                                    pattern={method === "pin" ? "[0-9]*" : undefined}
                                    value={newCred2}
                                    onChange={(e) => setNewCred2(e.target.value)}
                                    placeholder="Re-enter to confirm"
                                    className="mt-2 h-11 rounded-xl"
                                    required
                                    data-testid="rec-new-cred-confirm"
                                />
                            </div>

                            {err && (
                                <div className="text-sm text-rose-600 bg-rose-50 rounded-xl px-3 py-2"
                                    data-testid="rec-step2-error"
                                >
                                    {err}
                                    <div className="mt-1 text-[11px] text-rose-500/80">
                                        If this keeps failing, please contact your branch or Admin for a manual reset.
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => { setStep(1); setErr(""); }}
                                    className="rounded-full h-12 px-5"
                                    data-testid="rec-back"
                                >
                                    Back
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="flex-1 rounded-full h-12 bg-brand-navy hover:bg-brand-royal text-white text-base"
                                    data-testid="rec-step2-submit"
                                >
                                    <ShieldCheck className="h-4 w-4 mr-2" />
                                    {loading ? "Verifying…" : "Verify & Reset"}
                                </Button>
                            </div>
                        </form>
                    )}
                </Card>
            </main>

            <footer className="py-6 text-center text-xs text-slate-500">
                © {new Date().getFullYear()} Lokdhara Microfinance. All rights reserved.
            </footer>
        </div>
    );
}
